/**
 * Commitment Service
 * 
 * Handles all commitment-related API calls including:
 * - CRUD operations for commitments
 * - Promise management
 * - Question management
 * - P2P commitments
 * - Commitment filtering and details
 */

import api from './api';

// ============================================================================
// Types & Interfaces
// ============================================================================

export interface Commitment {
  _id?: string;
  [key: string]: any;
}

export interface PromiseData {
  _id?: string;
  [key: string]: any;
}

export interface Question {
  _id?: string;
  [key: string]: any;
}

export interface CommitmentFilters {
  tz?: string;
  status?: string;
  committer?: string;
  issuer?: string;
  [key: string]: any;
}

// ============================================================================
// Commitment CRUD Operations
// ============================================================================


/**
 * API: GET /api/v2.0/commitmentNew
 * Auth: yes
 * Gets commitments (new version)
 */
export async function getCommitmentsNew(params?: CommitmentFilters): Promise<any> {
  return api.get('/commitmentNew', { params });
}

/**
 * API: POST /api/v2.0/commitment
 * Auth: yes
 * Creates a new commitment
 */
export async function createCommitment(data: Commitment): Promise<any> {
  return api.post('/commitment', data);
}

/**
 * API: PUT /api/v2.0/commitment/:id
 * Auth: yes
 * Updates an existing commitment
 */
export async function updateCommitment(id: string, data: Commitment): Promise<any> {
  return api.put(`/commitment/${id}`, data);
}

/**
 * API: POST /api/v2.0/update/commitment/:id
 * Auth: yes
 * Updates a commitment (alternative endpoint)
 */
export async function updateCommitmentAlt(id: string, data: Commitment): Promise<any> {
  return api.post(`/update/commitment/${id}`, data);
}

/**
 * API: POST /api/v2.0/commitmentMultiArgs
 * Auth: no
 * Gets commitments with multiple arguments
 */
export async function getCommitmentMultiArgs(data: any): Promise<any> {
  return api.post('/commitmentMultiArgs', data);
}

// ============================================================================
// P2P Commitments
// ============================================================================

/**
 * API: POST /api/v2.0/createP2PCommitment
 * Auth: yes
 * Creates a peer-to-peer commitment
 */
export async function createP2PCommitment(data: any): Promise<any> {
  return api.post('/createP2PCommitment', data);
}

/**
 * API: POST /api/v2.0/testCreateClientCommitment
 * Auth: no
 * Test endpoint for creating client commitments
 */
export async function testCreateClientCommitment(data: any): Promise<any> {
  return api.post('/testCreateClientCommitment', data);
}

// ============================================================================
// Commitment Details
// ============================================================================

/**
 * API: GET /api/v2.0/getFullCommitmentDetails
 * Auth: no
 * Gets full details of a commitment
 */
export async function getFullCommitmentDetails(params: any): Promise<any> {
  return api.get('/getFullCommitmentDetails', { params });
}

/**
 * API: POST /api/v2.0/getFullCommitmentDetailsMulArg
 * Auth: no
 * Gets full commitment details with multiple arguments
 */
export async function getFullCommitmentDetailsMulArg(data: any): Promise<any> {
  return api.post('/getFullCommitmentDetailsMulArg', data);
}

// ============================================================================
// Promise CRUD Operations
// ============================================================================

/**
 * API: GET /api/v2.0/promise
 * Auth: yes
 * Gets all promises
 */
export async function getPromises(params?: any): Promise<any> {
  return api.get('/promise', { params });
}

/**
 * API: GET /api/v2.0/promise/:id
 * Auth: yes
 * Gets a specific promise by ID
 */
export async function getPromiseById(id: string): Promise<any> {
  return api.get(`/promise/${id}`);
}

/**
 * API: POST /api/v2.0/promise
 * Auth: yes
 * Creates a new promise
 */
export async function createPromise(data: PromiseData): Promise<any> {
  return api.post('/promise', data);
}

/**
 * API: PUT /api/v2.0/promise/:id
 * Auth: yes
 * Updates an existing promise
 */
export async function updatePromise(id: string, data: PromiseData): Promise<any> {
  return api.put(`/promise/${id}`, data);
}

/**
 * API: DELETE /api/v2.0/delete/promise
 * Auth: yes
 * Deletes a promise
 */
export async function deletePromise(params: { id: string }): Promise<any> {
  return api.delete('/delete/promise', { params });
}

/**
 * API: POST /api/v2.0/update/deletepromise/:id
 * Auth: no
 * Soft deletes a promise (marks as deleted)
 */
export async function softDeletePromise(id: string, data?: any): Promise<any> {
  return api.post(`/update/deletepromise/${id}`, data);
}

// ============================================================================
// Question CRUD Operations
// ============================================================================

/**
 * API: GET /api/v2.0/question
 * Auth: yes
 * Gets all questions
 */
export async function getQuestions(params?: any): Promise<any> {
  return api.get('/question', { params });
}

/**
 * API: GET /api/v2.0/questionNew
 * Auth: yes
 * Gets questions (new version)
 */
export async function getQuestionsNew(params?: any): Promise<any> {
  return api.get('/questionNew', { params });
}

/**
 * API: GET /api/v2.0/question/:id
 * Auth: yes
 * Gets a specific question by ID
 */
export async function getQuestionById(id: string): Promise<any> {
  return api.get(`/question/${id}`);
}

/**
 * API: GET /api/v2.0/questionNew/:id
 * Auth: yes
 * Gets a specific question by ID (new version)
 */
export async function getQuestionByIdNew(id: string): Promise<any> {
  return api.get(`/questionNew/${id}`);
}

/**
 * API: POST /api/v2.0/question
 * Auth: yes
 * Creates a new question
 */
export async function createQuestion(data: Question): Promise<any> {
  return api.post('/question', data);
}

/**
 * API: POST /api/v2.0/update/question
 * Auth: yes
 * Updates an existing question
 */
export async function updateQuestion(data: Question): Promise<any> {
  return api.post('/update/question', data);
}

/**
 * API: DELETE /api/v2.0/delete/question
 * Auth: yes
 * Deletes a question (DELETE method)
 */
export async function deleteQuestion(params: { id: string }): Promise<any> {
  return api.delete('/delete/question', { params });
}

/**
 * API: POST /api/v2.0/delete/question
 * Auth: yes
 * Deletes a question (POST method)
 */
export async function deleteQuestionPost(data: { id: string }): Promise<any> {
  return api.post('/delete/question', data);
}

// ============================================================================
// Action Required
// ============================================================================

/**
 * API: GET /api/v2.0/getActionRequired
 * Auth: yes
 * Gets items that require action from the user
 */
export async function getActionRequired(params?: any): Promise<any> {
  return api.get('/getActionRequired', { params });
}
